// react
import { useMemo } from 'react';

// api
import type { GetAlarmDto } from '@/api/generated/recalc/endpoints/v2/alarms';
import { useGetV2AlarmsQuery } from '@/api/generated/recalc/endpoints/v2/alarms';
import type { GetEnergyGroupDto } from '@/api/generated/recalc/endpoints/v2/energyGroups';

// relative
import { filterDeepDiveAlarms } from '../components/portfolio-dashboard/alarmScope';

interface UseDeepDiveAlarmsProps {
  energyGroups: GetEnergyGroupDto[] | undefined;
  accountId: number | undefined;
  enabled?: boolean;
}

interface UseDeepDiveAlarmsResult {
  alarms: GetAlarmDto[];
  isLoading: boolean;
  error: unknown;
}

/**
 * Hook to fetch all alarms and filter to show only those with associations
 * matching deepdive energygroups (non-portfolio energy groups)
 */
export const useDeepDiveAlarms = ({
  energyGroups,
  accountId,
  enabled = true,
}: UseDeepDiveAlarmsProps): UseDeepDiveAlarmsResult => {
  const hasLoadedEnergyGroups = energyGroups !== undefined;

  // Get deepdive energygroups (those that are not portfolio type)
  const deepDiveGroups = useMemo(() => {
    return energyGroups?.filter(
      (eg) => !eg.type?.toLowerCase().includes('portfolio'),
    );
  }, [energyGroups]);

  // Get list of deepdive energygroup IDs for filtering
  const deepDiveGroupIds = useMemo(() => {
    return (
      deepDiveGroups
        ?.map((eg) => eg.energyGroupId)
        .filter((groupId): groupId is number => typeof groupId === 'number') ??
      []
    );
  }, [deepDiveGroups]);

  // Fetch all alarms for the account
  const {
    data: allAlarms,
    isLoading,
    error,
  } = useGetV2AlarmsQuery(
    {
      accountIds: accountId ? [accountId] : undefined,
      orderBy: 'Created',
      orderDirection: 'desc',
    },
    { skip: !enabled || !accountId || !hasLoadedEnergyGroups },
  );

  // Filter alarms to show only those with associations matching deepdive energygroups
  const filteredAlarms = useMemo(() => {
    if (!allAlarms) {
      return [];
    }

    return filterDeepDiveAlarms(allAlarms, deepDiveGroupIds);
  }, [allAlarms, deepDiveGroupIds]);

  return {
    alarms: filteredAlarms,
    isLoading,
    error,
  };
};
